/**
 * Google Analytics 4 Utility Library
 * Core functions for tracking events, page views, and consent management
 *
 * @module lib/analytics
 * @description GA4 tracking with consent management for GDPR/CCPA compliance.
 *
 * Two rules govern every event emitted here:
 *
 * 1. **Never send from a non-production host.** Dev-server traffic on localhost /
 *    Tailscale was ~44% of all sessions in the production property (90-day audit,
 *    2026-07-26), inflating users, sessions and first_visit enough to make the
 *    reports unusable. `isTrackableHost()` is the gate.
 * 2. **Only send parameters GA4 can report on.** Universal Analytics params
 *    (`event_category`, `event_label`) are silently discarded by GA4, and `value` is
 *    reserved for *monetary* value — putting seconds or percentages there corrupts
 *    revenue metrics. Event names must also avoid GA4's automatically-collected
 *    names (`click`, `scroll`, `file_download`), or custom and automatic hits become
 *    indistinguishable in reports.
 */

// Configuration
const GA_MEASUREMENT_ID = process.env.GA_MEASUREMENT_ID || "G-W8DX96ES7R";
const DEBUG_MODE = process.env.GA_DEBUG === "true";

/** Hosts whose traffic belongs in the production property. */
const PRODUCTION_HOSTS = ["marketbrewer.com", "www.marketbrewer.com"];

// Storage keys
const CONSENT_KEY = "mb_analytics_consent";
const CONSENT_SHOWN_KEY = "mb_consent_shown";

/**
 * Whether this host may send hits to the production GA4 property.
 *
 * Set `GA_ALLOW_LOCAL=true` to deliberately send from a dev host when verifying
 * instrumentation — it pollutes the property, so unset it when finished.
 */
export const isTrackableHost = (): boolean => {
  if (typeof window === "undefined") return false;
  if (process.env.GA_ALLOW_LOCAL === "true") return true;
  return PRODUCTION_HOSTS.includes(window.location.hostname);
};

/**
 * Single funnel for every hit, so no tracking helper can bypass the host gate.
 */
const send = (eventName: string, params: Record<string, unknown>): void => {
  if (!isTrackableHost()) {
    if (DEBUG_MODE) {
      console.log("[GA4] suppressed (non-production host):", eventName, params);
    }
    return;
  }
  if (typeof window.gtag !== "function") return;

  window.gtag("event", eventName, params);

  if (DEBUG_MODE) {
    console.log("[GA4] Event:", eventName, params);
  }
};

/**
 * Check if user has given consent for analytics
 */
export const hasAnalyticsConsent = (): boolean => {
  if (typeof window === "undefined") return false;
  const consent = localStorage.getItem(CONSENT_KEY);
  return consent === "granted";
};

/**
 * Check if consent banner has been shown
 */
export const hasConsentBeenShown = (): boolean => {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(CONSENT_SHOWN_KEY) === "true";
};

/**
 * Set analytics consent status
 */
export const setAnalyticsConsent = (granted: boolean): void => {
  if (typeof window === "undefined") return;

  localStorage.setItem(CONSENT_KEY, granted ? "granted" : "denied");
  localStorage.setItem(CONSENT_SHOWN_KEY, "true");

  // Analytics always runs (US-based). Cookie consent controls ad tracking only.
  updateAdConsent(granted ? "granted" : "denied");
};

// Ad tracking consent — analytics_storage stays granted always
const updateAdConsent = (status: "granted" | "denied"): void => {
  if (typeof window.gtag !== "function") return;

  window.gtag("consent", "update", {
    ad_storage: status,
    ad_user_data: status,
    ad_personalization: status,
  });
};

/**
 * Initialize GA4 tracking
 */
export const initializeGA = (): void => {
  if (typeof window === "undefined") return;

  // Never load gtag.js off a dev host — the request itself creates a session.
  if (!isTrackableHost()) {
    if (DEBUG_MODE) {
      console.log(
        "[GA4] init skipped on non-production host:",
        window.location.hostname
      );
    }
    return;
  }

  // Prevent duplicate initialization
  if (
    document.querySelector(
      `script[src*="googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`
    )
  ) {
    return;
  }

  // Load gtag script
  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.async = true;
  document.head.appendChild(script);

  // Initialize dataLayer and gtag function
  // Must use `arguments` (not rest params) — gtag.js expects Arguments objects in dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false, // We handle page views manually for SPA
    // `anonymize_ip` omitted deliberately: it is a Universal Analytics setting and
    // GA4 always anonymizes IP, so passing it does nothing.
    // First-party analytics cookie: Lax. `SameSite=None` marks it cross-site, which
    // browsers restrict and which this single-domain site never needs.
    cookie_flags: "SameSite=Lax;Secure",
    // Marks hits for GA4 DebugView so instrumentation can be verified there.
    // Omitted entirely unless debugging — never set in normal production builds.
    ...(DEBUG_MODE ? { debug_mode: true } : {}),
  });

  // Reapply the visitor's stored ad-consent choice.
  //
  // index.html defaults ad consent to denied on every load. Without this, a
  // returning visitor who previously accepted was still sent as denied for the rest
  // of time, suppressing advertising attribution despite their saved choice.
  if (hasAnalyticsConsent()) {
    updateAdConsent("granted");
  }

  if (DEBUG_MODE) {
    console.log("[GA4] Initialized with ID:", GA_MEASUREMENT_ID);
  }
};

/**
 * Track page view
 *
 * `page_path` is not a GA4 parameter (it was Universal Analytics); GA4 derives the
 * path from `page_location`, so only real parameters are sent.
 */
export const trackPageView = (
  path: string,
  title: string,
  language: "en" | "es" = "en"
): void => {
  send("page_view", {
    page_title: title,
    page_location: typeof window !== "undefined" ? window.location.href : path,
    page_language: language,
  });
};

/**
 * Track custom event
 */
export const trackEvent = (
  eventName: string,
  params: Record<string, unknown> = {}
): void => {
  send(eventName, params);
};

/**
 * Entry-page context left behind by a generated SEO landing page.
 *
 * Those pages are plain documents; clicking through to a form is a full page load into
 * the React app, so without this the lead is reported against `/contact` and the landing
 * page that actually earned it looks like it produced nothing. GA4's session-scoped
 * landing-page dimension does record the entry, but this puts it directly on the
 * conversion event, so "which keyword and city produced leads" is answerable in the
 * Events report instead of only via an exploration.
 */
/**
 * sessionStorage survives as long as the tab does, which is longer than a GA session.
 * Past this window the landing page is no longer plausibly the reason for the lead, so
 * the attribution is dropped rather than credited to a stale page. Matches GA4's own
 * 30-minute session timeout.
 */
const ENTRY_TTL_MS = 30 * 60 * 1000;

const entryContext = (): Record<string, string> => {
  if (typeof window === "undefined") return {};
  try {
    const page = sessionStorage.getItem("mb_entry_page");
    if (!page) return {};

    // The timestamp must exist, parse, and place the landing in the past within the
    // TTL. A missing/garbled value or one in the future (clock changes) is treated as
    // expired — attribution is a claim, and an unverifiable claim is dropped.
    const at = Number(sessionStorage.getItem("mb_entry_at"));
    const age = Date.now() - at;
    if (!Number.isFinite(at) || at <= 0 || age < 0 || age > ENTRY_TTL_MS) return {};

    return {
      entry_page: page,
      entry_service: sessionStorage.getItem("mb_entry_service") || "",
      entry_location: sessionStorage.getItem("mb_entry_location") || "",
    };
  } catch {
    return {}; // private mode / storage disabled
  }
};

/**
 * Track a successful form submission (GA4 recommended `generate_lead` event).
 * Mark this as a key event in GA4 to make it a conversion.
 */
export const trackFormSubmission = (
  formName: string,
  language: "en" | "es" = "en",
  extra: Record<string, unknown> = {}
): void => {
  send("generate_lead", {
    ...entryContext(),
    // `lead_source` is part of GA4's own generate_lead schema, so contact vs booking
    // vs google-check leads stay separable. All three forms previously collapsed into
    // one undifferentiated generate_lead total.
    lead_source: formName,
    form_name: formName,
    form_id: formName,
    page_language: language,
    ...extra,
  });
};

/**
 * Track the first interaction with a lead form (fired once per mount).
 *
 * Named `lead_form_start`, never `form_start` — that name is reserved by GA4's
 * automatic Enhanced Measurement form events. With this event the completion
 * rate is a plain report: count(generate_lead) / count(lead_form_start).
 */
export const trackFormStart = (
  formName: string,
  language: "en" | "es" = "en"
): void => {
  send("lead_form_start", {
    ...entryContext(),
    form_name: formName,
    form_id: formName,
    page_language: language,
  });
};

/**
 * Track a started-but-not-submitted form when its component unmounts (SPA
 * navigation away). Tab-close abandons are not caught — beforeunload is
 * unreliable and pagehide fires on every mobile backgrounding, so this stays
 * scoped to what it can report honestly. `last_field` is the field NAME only,
 * never a value — no PII leaves the page.
 */
export const trackFormAbandon = (
  formName: string,
  language: "en" | "es",
  lastField: string,
  fieldsCompleted: number
): void => {
  send("lead_form_abandon", {
    form_name: formName,
    form_id: formName,
    page_language: language,
    last_field: lastField,
    fields_completed: fieldsCompleted,
  });
};

/**
 * Track a failed form submission, so submit-to-success drop-off is measurable.
 */
export const trackFormError = (
  formName: string,
  reason: string,
  language: "en" | "es" = "en"
): void => {
  send("form_error", {
    form_name: formName,
    form_id: formName,
    error_reason: reason,
    page_language: language,
  });
};

/**
 * Track CTA click
 */
export const trackCTAClick = (
  ctaText: string,
  destination: string,
  language: "en" | "es" = "en"
): void => {
  send("cta_click", {
    link_text: ctaText,
    link_url: destination,
    page_language: language,
  });
};

/**
 * Track outbound link click.
 *
 * Named `outbound_click`, not `click`: GA4 collects `click` automatically for
 * outbound links, so reusing the name makes custom and automatic hits
 * indistinguishable in every report.
 */
export const trackOutboundLink = (url: string): void => {
  send("outbound_click", {
    link_url: url,
    outbound: true,
  });
};

/**
 * Track file download. Named `download_click` to stay distinct from GA4's
 * automatic `file_download` enhanced-measurement event.
 */
export const trackDownload = (fileName: string, url = ""): void => {
  const extension = fileName.includes(".")
    ? fileName.split(".").pop() || ""
    : "";

  send("download_click", {
    file_name: fileName,
    file_extension: extension,
    link_url: url,
  });
};

/*
 * `trackServiceInterest` / `service_view` was removed deliberately (2026-07-26).
 *
 * It was exported but never called by any component, and it was redundant: every
 * service lives at its own URL (/services/local-seo, /services/photography), so
 * page_view already answers "which service draws interest". Firing a second event per
 * service pageview would have doubled the data and consumed a custom-dimension slot to
 * report something GA4 reports for free.
 */

/**
 * Track scroll depth milestone.
 *
 * Named `scroll_depth` to avoid GA4's automatic `scroll` event, and reports depth
 * in `percent_scrolled` (GA4's own parameter) instead of the reserved monetary
 * `value`.
 */
export const trackScrollDepth = (percentage: number): void => {
  send("scroll_depth", {
    percent_scrolled: percentage,
  });
};

/**
 * Track time on page.
 *
 * Seconds go in `seconds_spent`, never in `value` — `value` is GA4's monetary field
 * and populating it with seconds corrupts revenue reporting.
 */
export const trackTimeOnPage = (seconds: number, pagePath: string): void => {
  send("time_on_page", {
    seconds_spent: seconds,
    page_path_tracked: pagePath,
  });
};

/**
 * Track phone call click. Distinct event name so it can be marked a key event on
 * its own, without also converting every other link click.
 */
export const trackPhoneClick = (phoneNumber: string): void => {
  send("phone_click", {
    link_url: `tel:${phoneNumber}`,
    contact_method: "phone",
  });
};

/**
 * Track email click. Distinct event name — see `trackPhoneClick`.
 */
export const trackEmailClick = (email: string): void => {
  send("email_click", {
    link_url: `mailto:${email}`,
    contact_method: "email",
  });
};

/**
 * Track social media click.
 */
export const trackSocialClick = (platform: string, url: string): void => {
  send("social_click", {
    social_platform: platform,
    link_url: url,
  });
};

export { GA_MEASUREMENT_ID, CONSENT_KEY, CONSENT_SHOWN_KEY, PRODUCTION_HOSTS };
